<script setup lang="ts">
import { onMounted, onBeforeUnmount, ref, watch } from 'vue'

type Frame = {
  prices: number[]
  times: number[]
  matrix: number[][]
  candles: Array<{ t: number; o: number; h: number; l: number; c: number }>
  max_size: number
  reference_price?: number
  symbol?: string
  timeframe?: string
}

type Level = { price: number; size: number; pct: number; above: boolean; dist: number }

type Tip = {
  left: number
  top: number
  price: string
  time: string
  here: string
  herePct: string
  profile: string
  dist: string
  distUp: boolean
  hot: boolean
}

const props = defineProps<{
  frame: Frame
  levels?: Level[]
  showLevels?: boolean
  focus?: { price: number; at: number } | null
}>()

const root = ref<HTMLElement | null>(null)
const canvas = ref<HTMLCanvasElement | null>(null)
const tip = ref<Tip | null>(null)
const cursor = ref('grab')

const PAD = { l: 8, r: 92, t: 28, b: 28 }

let resizeObs: ResizeObserver | null = null
/** One pixel per heatmap cell, highest price on the top row. */
let heat: HTMLCanvasElement | null = null
let raf = 0
let cssW = 0
let cssH = 0

/** Visible window: x in column-index units, y in price units. */
const view = { x0: 0, x1: 1, y0: 0, y1: 1 }
let hover: { x: number; y: number } | null = null
let drag: null | { mode: 'pan' | 'scaleY'; x: number; y: number; start: typeof view } = null
let highlight: number | null = null

/** Purple → cyan → yellow intensity ramp. */
function colorFor(t: number): [number, number, number, number] {
  const x = Math.max(0, Math.min(1, t))
  if (x <= 0) return [20, 12, 40, 0]
  if (x < 0.25) {
    const u = x / 0.25
    return [lerp(40, 70, u), lerp(20, 40, u), lerp(90, 160, u), Math.round(40 + 80 * u)]
  }
  if (x < 0.55) {
    const u = (x - 0.25) / 0.3
    return [lerp(70, 20, u), lerp(40, 180, u), lerp(160, 170, u), Math.round(120 + 80 * u)]
  }
  if (x < 0.8) {
    const u = (x - 0.55) / 0.25
    return [lerp(20, 180, u), lerp(180, 220, u), lerp(170, 60, u), 220]
  }
  const u = (x - 0.8) / 0.2
  return [lerp(180, 255, u), lerp(220, 250, u), lerp(60, 40, u), 255]
}

function lerp(a: number, b: number, t: number) {
  return Math.round(a + (b - a) * t)
}

function priceStep(f: Frame): number {
  const p = f.prices
  if (p.length > 1) return (p[p.length - 1] - p[0]) / (p.length - 1)
  return Math.max(1e-9, Math.abs(p[0] || 1) * 0.001)
}

function bounds(f: Frame) {
  const s = priceStep(f)
  return {
    xMin: 0,
    xMax: Math.max(1, f.times.length),
    yMin: f.prices[0] - s / 2,
    yMax: f.prices[f.prices.length - 1] + s / 2,
  }
}

function plotSize() {
  return { w: cssW - PAD.l - PAD.r, h: cssH - PAD.t - PAD.b }
}

function ready(): boolean {
  const f = props.frame
  return !!(f?.prices?.length && f?.times?.length && f?.matrix?.length)
}

function buildHeat() {
  heat = null
  if (!ready()) return
  const f = props.frame
  const nT = f.times.length
  const nP = f.prices.length
  const off = document.createElement('canvas')
  off.width = nT
  off.height = nP
  const ctx = off.getContext('2d')
  if (!ctx) return
  const img = ctx.createImageData(nT, nP)
  const peak = Math.max(1e-9, Number(f.max_size) || 1)
  for (let i = 0; i < nT; i++) {
    const col = f.matrix[i] || []
    for (let j = 0; j < nP; j++) {
      const v = Number(col[j] || 0)
      if (v <= 0) continue
      const [r, g, b, a] = colorFor(v / peak)
      const k = ((nP - 1 - j) * nT + i) * 4
      img.data[k] = r
      img.data[k + 1] = g
      img.data[k + 2] = b
      img.data[k + 3] = a
    }
  }
  ctx.putImageData(img, 0, 0)
  heat = off
}

function clampView() {
  const f = props.frame
  const b = bounds(f)
  const fullW = b.xMax - b.xMin
  const w = Math.max(Math.min(fullW, 8), Math.min(fullW, view.x1 - view.x0))
  const x0 = Math.max(b.xMin, Math.min(b.xMax - w, view.x0))
  view.x0 = x0
  view.x1 = x0 + w

  const fullH = b.yMax - b.yMin
  const h = Math.max(Math.min(fullH, priceStep(f) * 6), Math.min(fullH, view.y1 - view.y0))
  const y0 = Math.max(b.yMin, Math.min(b.yMax - h, view.y0))
  view.y0 = y0
  view.y1 = y0 + h
}

function resetView() {
  if (!ready()) return
  const b = bounds(props.frame)
  view.x0 = b.xMin
  view.x1 = b.xMax
  view.y0 = b.yMin
  view.y1 = b.yMax
  highlight = null
  schedule()
}

function zoomBoth(factor: number) {
  const cx = (view.x0 + view.x1) / 2
  const cy = (view.y0 + view.y1) / 2
  const hw = ((view.x1 - view.x0) / 2) * factor
  const hh = ((view.y1 - view.y0) / 2) * factor
  view.x0 = cx - hw
  view.x1 = cx + hw
  view.y0 = cy - hh
  view.y1 = cy + hh
  clampView()
  schedule()
}

function schedule() {
  if (!raf) raf = requestAnimationFrame(draw)
}

function draw() {
  raf = 0
  const el = canvas.value
  const host = root.value
  const f = props.frame
  if (!el || !host || !ready()) return
  if (!heat) buildHeat()
  if (!heat) return

  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  cssW = Math.max(320, host.clientWidth)
  cssH = Math.max(320, host.clientHeight)
  if (el.width !== Math.floor(cssW * dpr) || el.height !== Math.floor(cssH * dpr)) {
    el.width = Math.floor(cssW * dpr)
    el.height = Math.floor(cssH * dpr)
    el.style.width = `${cssW}px`
    el.style.height = `${cssH}px`
  }
  const ctx = el.getContext('2d')
  if (!ctx) return
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

  const { w: plotW, h: plotH } = plotSize()
  const nT = f.times.length
  const nP = f.prices.length
  const step = priceStep(f)
  const b = bounds(f)
  const peak = Math.max(1e-9, Number(f.max_size) || 1)
  const X = (i: number) => PAD.l + ((i - view.x0) / (view.x1 - view.x0)) * plotW
  const Y = (p: number) => PAD.t + ((view.y1 - p) / (view.y1 - view.y0)) * plotH
  const colW = plotW / (view.x1 - view.x0)

  ctx.fillStyle = '#0b0714'
  ctx.fillRect(0, 0, cssW, cssH)

  // Heatmap (visible part of the one-pixel-per-cell image)
  ctx.save()
  ctx.beginPath()
  ctx.rect(PAD.l, PAD.t, plotW, plotH)
  ctx.clip()
  ctx.imageSmoothingEnabled = false
  ctx.drawImage(
    heat,
    view.x0, (b.yMax - view.y1) / step, view.x1 - view.x0, (view.y1 - view.y0) / step,
    PAD.l, PAD.t, plotW, plotH,
  )

  // Candles
  const candles = f.candles || []
  if (candles.length) {
    const byT = new Map(candles.map((c) => [c.t, c]))
    const from = Math.max(0, Math.floor(view.x0))
    const to = Math.min(nT - 1, Math.ceil(view.x1))
    for (let i = from; i <= to; i++) {
      const c = byT.get(f.times[i])
      if (!c) continue
      const x = X(i + 0.5)
      const up = c.c >= c.o
      ctx.strokeStyle = up ? 'rgba(52,211,153,0.85)' : 'rgba(248,113,113,0.85)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(x, Y(c.h))
      ctx.lineTo(x, Y(c.l))
      ctx.stroke()
      ctx.fillStyle = up ? 'rgba(52,211,153,0.9)' : 'rgba(248,113,113,0.9)'
      const bw = Math.max(2, colW * 0.56)
      ctx.fillRect(x - bw / 2, Math.min(Y(c.o), Y(c.c)), bw, Math.max(1, Math.abs(Y(c.c) - Y(c.o))))
    }
  }

  // Strongest-level lines
  if (props.showLevels && props.levels?.length) {
    ctx.setLineDash([5, 4])
    ctx.lineWidth = 1
    for (const lv of props.levels) {
      if (lv.price < view.y0 || lv.price > view.y1) continue
      const y = Math.round(Y(lv.price)) + 0.5
      ctx.strokeStyle = 'rgba(250,204,21,0.75)'
      ctx.beginPath()
      ctx.moveTo(PAD.l, y)
      ctx.lineTo(PAD.l + plotW, y)
      ctx.stroke()
    }
    ctx.setLineDash([])
  }

  if (highlight !== null && highlight >= view.y0 && highlight <= view.y1) {
    const y = Math.round(Y(highlight)) + 0.5
    ctx.strokeStyle = 'rgba(255,255,255,0.95)'
    ctx.lineWidth = 1.5
    ctx.beginPath()
    ctx.moveTo(PAD.l, y)
    ctx.lineTo(PAD.l + plotW, y)
    ctx.stroke()
  }

  // Crosshair
  if (hover && !drag && hover.x >= PAD.l && hover.x <= PAD.l + plotW && hover.y >= PAD.t && hover.y <= PAD.t + plotH) {
    ctx.setLineDash([3, 3])
    ctx.strokeStyle = 'rgba(228,228,231,0.55)'
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(Math.round(hover.x) + 0.5, PAD.t)
    ctx.lineTo(Math.round(hover.x) + 0.5, PAD.t + plotH)
    ctx.moveTo(PAD.l, Math.round(hover.y) + 0.5)
    ctx.lineTo(PAD.l + plotW, Math.round(hover.y) + 0.5)
    ctx.stroke()
    ctx.setLineDash([])
  }
  ctx.restore()

  // Right-side liquidity profile (latest column)
  const lastCol = f.matrix[nT - 1] || []
  let maxProf = 0
  for (const v of lastCol) maxProf = Math.max(maxProf, Number(v) || 0)
  maxProf = Math.max(maxProf, 1e-9)
  const ref = Number(f.reference_price || 0)
  ctx.save()
  ctx.beginPath()
  ctx.rect(PAD.l + plotW, PAD.t, PAD.r, plotH)
  ctx.clip()
  for (let j = 0; j < nP; j++) {
    const v = Number(lastCol[j] || 0)
    const p = f.prices[j]
    if (v <= 0 || p + step / 2 < view.y0 || p - step / 2 > view.y1) continue
    const top = Y(p + step / 2)
    const hgt = Math.max(1, Y(p - step / 2) - top - 0.5)
    ctx.fillStyle = p >= ref ? 'rgba(250,204,21,0.75)' : 'rgba(248,113,113,0.7)'
    ctx.fillRect(PAD.l + plotW + 8, top, (v / maxProf) * (PAD.r - 16), hgt)
  }
  ctx.restore()

  // Labels
  ctx.font = '11px ui-monospace, SFMono-Regular, Menlo, monospace'
  ctx.fillStyle = 'rgba(212,212,216,0.85)'
  ctx.textAlign = 'left'
  ctx.fillText(`${f.symbol || ''} · ${f.timeframe || ''}`, PAD.l, 16)
  ctx.textAlign = 'right'
  ctx.fillText(formatLiq(peak), cssW - 10, 16)

  ctx.textAlign = 'left'
  for (const t of [0, 0.25, 0.5, 0.75, 1]) {
    const p = view.y1 - t * (view.y1 - view.y0)
    ctx.fillStyle = 'rgba(161,161,170,0.8)'
    ctx.fillText(formatPrice(p), PAD.l + plotW + 8, Math.min(PAD.t + plotH - 2, Math.max(PAD.t + 10, Y(p) + 3)))
  }

  ctx.textAlign = 'center'
  ctx.fillStyle = 'rgba(161,161,170,0.8)'
  for (const t of [0.02, 0.35, 0.65, 0.98]) {
    const i = Math.min(nT - 1, Math.max(0, Math.floor(view.x0 + t * (view.x1 - view.x0))))
    ctx.fillText(formatTime(f.times[i]), PAD.l + t * plotW, cssH - 10)
  }

  // Level price pills on the axis
  if (props.showLevels && props.levels?.length) {
    ctx.textAlign = 'left'
    for (const lv of props.levels) {
      if (lv.price < view.y0 || lv.price > view.y1) continue
      pill(ctx, formatPrice(lv.price), PAD.l + plotW + 4, Y(lv.price), 'rgba(250,204,21,0.95)', '#1a1405')
    }
  }
  if (highlight !== null && highlight >= view.y0 && highlight <= view.y1) {
    pill(ctx, formatPrice(highlight), PAD.l + plotW + 4, Y(highlight), '#ffffff', '#0b0714')
  }

  // Crosshair pills
  if (hover && !drag && hover.x >= PAD.l && hover.x <= PAD.l + plotW && hover.y >= PAD.t && hover.y <= PAD.t + plotH) {
    const p = view.y1 - ((hover.y - PAD.t) / plotH) * (view.y1 - view.y0)
    pill(ctx, formatPrice(p), PAD.l + plotW + 4, hover.y, '#3f3f46', '#fafafa')
    const i = Math.min(nT - 1, Math.max(0, Math.floor(view.x0 + ((hover.x - PAD.l) / plotW) * (view.x1 - view.x0))))
    ctx.textAlign = 'center'
    const label = formatTime(f.times[i])
    const w = ctx.measureText(label).width + 12
    ctx.fillStyle = '#3f3f46'
    ctx.fillRect(hover.x - w / 2, cssH - 24, w, 18)
    ctx.fillStyle = '#fafafa'
    ctx.fillText(label, hover.x, cssH - 11)
  }
}

function pill(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, bg: string, fg: string) {
  ctx.font = '11px ui-monospace, SFMono-Regular, Menlo, monospace'
  const w = ctx.measureText(text).width + 10
  ctx.fillStyle = bg
  ctx.fillRect(x, y - 9, w, 18)
  ctx.fillStyle = fg
  ctx.textAlign = 'left'
  ctx.fillText(text, x + 5, y + 4)
}

function updateTip(x: number, y: number) {
  const f = props.frame
  const { w: plotW, h: plotH } = plotSize()
  if (x < PAD.l || x > PAD.l + plotW || y < PAD.t || y > PAD.t + plotH) {
    tip.value = null
    return
  }
  const nT = f.times.length
  const nP = f.prices.length
  const step = priceStep(f)
  const i = Math.min(nT - 1, Math.max(0, Math.floor(view.x0 + ((x - PAD.l) / plotW) * (view.x1 - view.x0))))
  const p = view.y1 - ((y - PAD.t) / plotH) * (view.y1 - view.y0)
  const j = Math.min(nP - 1, Math.max(0, Math.round((p - f.prices[0]) / step)))
  const peak = Math.max(1e-9, Number(f.max_size) || 1)
  const v = Number(f.matrix[i]?.[j] || 0)
  const prof = Number(f.matrix[nT - 1]?.[j] || 0)
  const lastCandle = f.candles?.length ? f.candles[f.candles.length - 1].c : 0
  const ref = Number(f.reference_price || lastCandle || 0)
  const dist = ref ? (f.prices[j] / ref - 1) * 100 : 0
  const flipX = x > cssW - 260
  const flipY = y > cssH - 150
  tip.value = {
    left: flipX ? x - 232 : x + 16,
    top: flipY ? y - 128 : y + 16,
    price: formatPrice(f.prices[j]),
    time: formatTime(f.times[i]),
    here: formatLiq(v),
    herePct: `${Math.round((v / peak) * 100)}%`,
    profile: formatLiq(prof),
    dist: `${dist >= 0 ? '+' : ''}${dist.toFixed(2)}%`,
    distUp: dist >= 0,
    hot: v / peak >= 0.8,
  }
}

function localPoint(e: PointerEvent | WheelEvent | MouseEvent) {
  const rect = canvas.value!.getBoundingClientRect()
  return { x: e.clientX - rect.left, y: e.clientY - rect.top }
}

function onWheel(e: WheelEvent) {
  if (!ready()) return
  e.preventDefault()
  const { x, y } = localPoint(e)
  const { w: plotW, h: plotH } = plotSize()
  const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15
  if (e.shiftKey || x > PAD.l + plotW) {
    const py = view.y1 - ((Math.max(PAD.t, Math.min(PAD.t + plotH, y)) - PAD.t) / plotH) * (view.y1 - view.y0)
    view.y0 = py - (py - view.y0) * factor
    view.y1 = py + (view.y1 - py) * factor
  } else {
    const ix = view.x0 + ((Math.max(PAD.l, Math.min(PAD.l + plotW, x)) - PAD.l) / plotW) * (view.x1 - view.x0)
    view.x0 = ix - (ix - view.x0) * factor
    view.x1 = ix + (view.x1 - ix) * factor
  }
  clampView()
  hover = { x, y }
  updateTip(x, y)
  schedule()
}

function onPointerDown(e: PointerEvent) {
  if (!ready()) return
  const { x, y } = localPoint(e)
  const { w: plotW } = plotSize()
  drag = { mode: x > PAD.l + plotW ? 'scaleY' : 'pan', x, y, start: { ...view } }
  cursor.value = drag.mode === 'pan' ? 'grabbing' : 'ns-resize'
  tip.value = null
  canvas.value?.setPointerCapture(e.pointerId)
}

function onPointerMove(e: PointerEvent) {
  if (!ready()) return
  const { x, y } = localPoint(e)
  const { w: plotW, h: plotH } = plotSize()
  if (drag) {
    const s = drag.start
    if (drag.mode === 'pan') {
      const dx = ((x - drag.x) / plotW) * (s.x1 - s.x0)
      const dy = ((y - drag.y) / plotH) * (s.y1 - s.y0)
      view.x0 = s.x0 - dx
      view.x1 = s.x1 - dx
      view.y0 = s.y0 + dy
      view.y1 = s.y1 + dy
    } else {
      const factor = Math.exp((y - drag.y) * 0.01)
      const cy = (s.y0 + s.y1) / 2
      const half = ((s.y1 - s.y0) / 2) * factor
      view.y0 = cy - half
      view.y1 = cy + half
    }
    clampView()
    schedule()
    return
  }
  hover = { x, y }
  cursor.value = x > PAD.l + plotW ? 'ns-resize' : 'crosshair'
  updateTip(x, y)
  schedule()
}

function onPointerUp(e: PointerEvent) {
  if (canvas.value?.hasPointerCapture(e.pointerId)) canvas.value.releasePointerCapture(e.pointerId)
  drag = null
  cursor.value = 'crosshair'
  schedule()
}

function onPointerLeave() {
  if (drag) return
  hover = null
  tip.value = null
  schedule()
}

function formatPrice(p: number) {
  if (p >= 1000) return p.toLocaleString('en-US', { maximumFractionDigits: 1 })
  if (p >= 1) return p.toLocaleString('en-US', { maximumFractionDigits: 4 })
  return p.toLocaleString('en-US', { maximumFractionDigits: 6 })
}

function formatLiq(v: number) {
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`
  return v.toFixed(0)
}

function formatTime(ts: number) {
  const sec = ts > 1e12 ? Math.floor(ts / 1000) : ts
  const d = new Date(sec * 1000)
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

onMounted(() => {
  buildHeat()
  resetView()
  canvas.value?.addEventListener('wheel', onWheel, { passive: false })
  if (root.value && typeof ResizeObserver !== 'undefined') {
    resizeObs = new ResizeObserver(() => schedule())
    resizeObs.observe(root.value)
  }
})

onBeforeUnmount(() => {
  canvas.value?.removeEventListener('wheel', onWheel)
  resizeObs?.disconnect()
  resizeObs = null
  if (raf) cancelAnimationFrame(raf)
})

watch(() => props.frame, () => {
  buildHeat()
  tip.value = null
  resetView()
})

watch(() => [props.levels, props.showLevels], () => schedule(), { deep: true })

watch(() => props.focus, (focus) => {
  if (!focus || !ready()) return
  const b = bounds(props.frame)
  const fullH = b.yMax - b.yMin
  const h = Math.min(view.y1 - view.y0, fullH * 0.3)
  view.y0 = focus.price - h / 2
  view.y1 = focus.price + h / 2
  highlight = focus.price
  clampView()
  schedule()
})
</script>

<template>
  <div ref="root" class="relative h-full w-full select-none overflow-hidden rounded-2xl bg-[#0b0714]">
    <canvas
      ref="canvas"
      class="block h-full w-full touch-none"
      :style="{ cursor }"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointercancel="onPointerUp"
      @pointerleave="onPointerLeave"
      @dblclick="resetView"
    />

    <div class="absolute left-2 top-7 flex items-center gap-1">
      <button type="button" class="rounded-md bg-zinc-900/80 px-2 py-0.5 text-sm text-zinc-200 ring-1 ring-white/10 hover:bg-zinc-800" :title="__('Zoom in')" @click="zoomBoth(1 / 1.4)">+</button>
      <button type="button" class="rounded-md bg-zinc-900/80 px-2 py-0.5 text-sm text-zinc-200 ring-1 ring-white/10 hover:bg-zinc-800" :title="__('Zoom out')" @click="zoomBoth(1.4)">−</button>
      <button type="button" class="rounded-md bg-zinc-900/80 px-2 py-0.5 text-xs text-zinc-200 ring-1 ring-white/10 hover:bg-zinc-800" @click="resetView">{{ __('Reset view') }}</button>
    </div>

    <div class="pointer-events-none absolute bottom-8 left-2 rounded bg-zinc-900/70 px-2 py-0.5 text-[10px] text-zinc-400">
      {{ __('Scroll: zoom time · Shift+scroll or drag the price axis: zoom price · Drag: move · Double-click: reset') }}
    </div>

    <div
      v-if="tip"
      class="pointer-events-none absolute z-10 w-[216px] rounded-xl bg-zinc-950/95 px-3 py-2 text-[11px] leading-5 text-zinc-200 shadow-xl ring-1"
      :class="tip.hot ? 'ring-yellow-400/70' : 'ring-white/15'"
      :style="{ left: `${tip.left}px`, top: `${tip.top}px` }"
      dir="rtl"
    >
      <div class="flex items-center justify-between gap-2">
        <span class="text-zinc-400">{{ __('Price') }}</span>
        <span class="font-mono text-sm font-semibold text-white" dir="ltr">{{ tip.price }}</span>
      </div>
      <div class="flex items-center justify-between gap-2">
        <span class="text-zinc-400">{{ __('Time') }}</span>
        <span class="font-mono" dir="ltr">{{ tip.time }}</span>
      </div>
      <div class="flex items-center justify-between gap-2">
        <span class="text-zinc-400">{{ __('Liquidity here') }}</span>
        <span class="font-mono" dir="ltr">{{ tip.here }} · {{ tip.herePct }}</span>
      </div>
      <div class="flex items-center justify-between gap-2">
        <span class="text-zinc-400">{{ __('Latest liquidity at this price') }}</span>
        <span class="font-mono" dir="ltr">{{ tip.profile }}</span>
      </div>
      <div class="flex items-center justify-between gap-2">
        <span class="text-zinc-400">{{ __('From current price') }}</span>
        <span class="font-mono" :class="tip.distUp ? 'text-amber-300' : 'text-rose-300'" dir="ltr">{{ tip.dist }}</span>
      </div>
      <div v-if="tip.hot" class="mt-1 text-center text-[10px] font-semibold text-yellow-300">{{ __('Hot zone') }}</div>
    </div>
  </div>
</template>
